import {
    type ButtonHTMLAttributes,
    type SelectHTMLAttributes,
    type ReactNode,
    useEffect,
    useRef,
} from 'react';
import { cn } from '../lib/utils';
import type { StageStatus } from '../lib/database.types';
import { STATUS_LABELS } from '../lib/database.types';

// -----------------------------------------------------------------------------
// Button
// -----------------------------------------------------------------------------

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
    size?: 'sm' | 'md' | 'lg';
}

export function Button({
    variant = 'secondary',
    size = 'md',
    className,
    ...props
}: ButtonProps) {
    return (
        <button
            {...props}
            className={cn(
                'inline-flex items-center justify-center gap-1.5 font-medium rounded transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                size === 'sm' && 'text-xs px-2.5 py-1.5',
                size === 'md' && 'text-sm px-3.5 py-2',
                size === 'lg' && 'text-base px-4 py-2.5',
                variant === 'primary' &&
                    'bg-signal-500 text-graphite-950 hover:bg-signal-400',
                variant === 'secondary' &&
                    'bg-graphite-200 text-graphite-900 hover:bg-graphite-300 border border-graphite-300 dark:bg-graphite-800 dark:text-graphite-100 dark:hover:bg-graphite-700 dark:border-graphite-700',
                variant === 'ghost' &&
                    'text-graphite-700 hover:text-graphite-900 hover:bg-graphite-200 dark:text-graphite-300 dark:hover:text-graphite-50 dark:hover:bg-graphite-800',
                variant === 'danger' &&
                    'bg-amber-500/10 text-amber-600 border border-amber-500/30 hover:bg-amber-500/20 dark:text-amber-400',
                className
            )}
        />
    );
}

// -----------------------------------------------------------------------------
// StatusBadge
// -----------------------------------------------------------------------------
// Color language Andrea reads on the board:
//   pending     -> yellow
//   scheduled   -> blue (signal)
//   in_progress -> orange
//   complete    -> green (done)
//   on_hold     -> purple (hold)
//
// SOLID FILLS, dark text. These were 10-15% tints with coloured text, which
// Andrea called out on 29 Jul: "Can we please make the colors for each task
// more bold? Right now they are light, more pastel."
//
// Her spreadsheet is the reference. Every screenshot she has sent shows solid
// cell fills with black text, and she sorts by those colours daily, so the
// badge has to be legible at a glance across a screen of rows rather than
// pretty up close.
//
// Dark text on the fill rather than white in both themes: white on cyan and
// white on yellow both fail contrast at 9px, and keeping one treatment means
// the board reads identically whichever theme someone is in.
//
// in_progress lost its animate-pulse here. Pulsing a pale tint drew the eye to
// something easy to miss; pulsing a solid orange block across dozens of rows is
// just noise.

const STATUS_STYLES: Record<StageStatus, { label: string; classes: string }> = {
    pending: {
        label: STATUS_LABELS.pending,
        classes: 'bg-yellow-400 text-graphite-950 border-yellow-500',
    },
    scheduled: {
        label: STATUS_LABELS.scheduled,
        classes: 'bg-signal-400 text-graphite-950 border-signal-600',
    },
    in_progress: {
        label: STATUS_LABELS.in_progress,
        classes: 'bg-orange-400 text-graphite-950 border-orange-500',
    },
    complete: {
        label: STATUS_LABELS.complete,
        classes: 'bg-done-500 text-graphite-950 border-done-600',
    },
    on_hold: {
        label: STATUS_LABELS.on_hold,
        classes: 'bg-hold-500 text-graphite-950 border-hold-600',
    },
};

export function StatusBadge({ status, size = 'md' }: { status: StageStatus; size?: 'sm' | 'md' }) {
    const cfg = STATUS_STYLES[status];
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 border rounded font-mono font-semibold uppercase tracking-wider',
                size === 'sm' && 'text-[9px] px-1.5 py-0.5',
                size === 'md' && 'text-[10px] px-2 py-0.5',
                cfg.classes
            )}
        >
            {cfg.label}
        </span>
    );
}

// -----------------------------------------------------------------------------
// Dialog - simple centered modal, no animation complexity
// -----------------------------------------------------------------------------

interface DialogProps {
    open: boolean;
    onClose: () => void;
    title: string;
    subtitle?: string;
    children: ReactNode;
    footer?: ReactNode;
    maxWidth?: 'sm' | 'md' | 'lg';
}

export function Dialog({ open, onClose, title, subtitle, children, footer, maxWidth = 'md' }: DialogProps) {
    const dialogRef = useRef<HTMLDivElement>(null);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    // Prevent body scroll when open
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="dialog-title"
                className={cn(
                    'w-full bg-white border border-graphite-200 dark:bg-graphite-900 dark:border-graphite-700 rounded shadow-card-md',
                    maxWidth === 'sm' && 'max-w-sm',
                    maxWidth === 'md' && 'max-w-md',
                    maxWidth === 'lg' && 'max-w-2xl'
                )}
            >
                <div className="px-5 py-4 border-b border-graphite-200 dark:border-graphite-800">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <h2 id="dialog-title" className="font-display font-semibold text-lg text-graphite-900 dark:text-graphite-50">
                                {title}
                            </h2>
                            {subtitle ? (
                                <p className="font-mono text-xs text-graphite-600 dark:text-graphite-400 mt-0.5">{subtitle}</p>
                            ) : null}
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            className="text-graphite-500 hover:text-graphite-900 dark:hover:text-graphite-200 transition-colors -m-1 p-1"
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.5" />
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="px-5 py-4">{children}</div>
                {footer ? (
                    <div className="px-5 py-3 border-t border-graphite-200 dark:border-graphite-800 flex justify-end gap-2 bg-graphite-100/50 dark:bg-graphite-950/50">
                        {footer}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Select, Input, Label primitives
// -----------------------------------------------------------------------------

export function Label({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
    return (
        <label
            htmlFor={htmlFor}
            className="block font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-600 dark:text-graphite-400 mb-1.5"
        >
            {children}
        </label>
    );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
    return (
        <input
            {...props}
            className={cn(
                'w-full bg-graphite-50 border border-graphite-300 text-graphite-900 placeholder-graphite-400 dark:bg-graphite-950 dark:border-graphite-700 dark:text-graphite-50 dark:placeholder-graphite-600 rounded px-3 py-2 text-sm focus:border-signal-500 focus:ring-0 focus:outline-none transition-colors',
                props.className
            )}
        />
    );
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
    return (
        <textarea
            {...props}
            className={cn(
                'w-full bg-graphite-50 border border-graphite-300 text-graphite-900 placeholder-graphite-400 dark:bg-graphite-950 dark:border-graphite-700 dark:text-graphite-50 dark:placeholder-graphite-600 rounded px-3 py-2 text-sm focus:border-signal-500 focus:ring-0 focus:outline-none transition-colors resize-none',
                props.className
            )}
        />
    );
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
    return (
        <select
            {...props}
            className={cn(
                'w-full bg-graphite-50 border border-graphite-300 text-graphite-900 dark:bg-graphite-950 dark:border-graphite-700 dark:text-graphite-50 rounded px-3 py-2 text-sm focus:border-signal-500 focus:ring-0 focus:outline-none transition-colors',
                props.className
            )}
        />
    );
}

// -----------------------------------------------------------------------------
// EmptyState
// -----------------------------------------------------------------------------

export function EmptyState({
    title,
    description,
    action,
}: {
    title: string;
    description?: string;
    action?: ReactNode;
}) {
    return (
        <div className="border border-dashed border-graphite-300 dark:border-graphite-800 rounded px-6 py-10 text-center">
            <p className="font-display font-semibold text-graphite-800 dark:text-graphite-200">{title}</p>
            {description ? (
                <p className="mt-1 text-sm text-graphite-500">{description}</p>
            ) : null}
            {action ? <div className="mt-4">{action}</div> : null}
        </div>
    );
}
